import React, { useState, useContext } from 'react';
import SearchForm from '../components/SearchForm';
import ResultsPreview from '../components/ResultsPreview';
import DownloadModal from '../components/DownloadModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { AuthContext } from '../context/AuthContext';

const Home = () => {
    const { user, plan } = useContext(AuthContext);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [abortController, setAbortController] = useState(null);

    const handleCancel = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setLoading(false);
            setErrorMessage('Búsqueda cancelada por el usuario.');
        }
    };

    const handleSearch = async (params) => {
        // Crear un nuevo AbortController para esta búsqueda
        const controller = new AbortController();
        setAbortController(controller);

        setLoading(true);
        setSearchParams(params);
        setErrorMessage(''); // Limpiar mensaje de error anterior
        try {
            const searchPlaces = httpsCallable(functions, 'searchPlaces');
            const response = await searchPlaces(params);
            const places = response.data.places || [];
            // Filtro adicional por ciudad para evitar resultados de otras ubicaciones
            const cityQuery = (params.address || '').split(',')[0].trim().toLowerCase();
            const filtered = cityQuery && cityQuery.length > 2
                ? places.filter((p) => {
                    const address = (p.address || '').toLowerCase();
                    const matches = address.includes(cityQuery);
                    return matches;
                })
                : places;

            setResults(filtered);
            if (filtered.length === 0) {
                setErrorMessage('No se encontraron resultados para tu búsqueda. Intenta con una ciudad o categoría diferente.');
            }
            } catch (error) {
            // Mensajes específicos según el error
            if (error.message.includes('API key not configured')) {
                setErrorMessage('Error interno: La API no está configurada correctamente. Contacta al soporte.');
            } else if (error.message.includes('Search failed')) {
                setErrorMessage('Error al buscar en Google Places. Puede ser un problema de cuota o configuración.');
            } else if (error.code === 'functions/cancelled') {
                setErrorMessage('La búsqueda fue cancelada.');
            } else if (error.code === 'functions/deadline-exceeded') {
                setErrorMessage('La búsqueda tardó demasiado. Inténtalo de nuevo.');
            } else if (error.code === 'functions/unauthenticated') {
                setErrorMessage('Debes iniciar sesión para buscar contactos.');
            } else if (error.code === 'functions/internal') {
                setErrorMessage('Error interno del servidor. Inténtalo más tarde.');
            } else {
                setErrorMessage('Error al buscar contactos: ' + (error.message || 'Error desconocido'));
            }
        } finally {
            setLoading(false);
            setAbortController(null);
        }
    };

    const handleDownload = async () => {
        setModalOpen(false);
        if (!results.length) {
            alert('No hay resultados para descargar.');
            return;
        }
        try {
            const generateCsv = httpsCallable(functions, 'generateCsv');
            const response = await generateCsv({ places: results });
            const csv = response.data.csv;
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'leads.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Error al descargar CSV: ' + (error.message || 'Error desconocido'));
        }
    };

    return (
        <div className="fade-in page-content">
            <h1>Buscar Contactos</h1>
            <p className="page-lead">
                Encontrá y gestioná contactos de forma eficiente: clientes potenciales, proveedores y más
            </p>
            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <LoadingMessages />
                    <button type="button" className="btn-cancel-search" onClick={handleCancel}>
                        Detener búsqueda
                    </button>
                </div>
            )}
            <SearchForm onSearch={handleSearch} loading={loading} />
            {user && (
                <p className="search-plan-hint">
                    {plan === 'premium' ? 'Hasta 50 resultados por búsqueda (Premium).' : 'Hasta 10 resultados por búsqueda. Hacete Premium para obtener hasta 50.'}
                </p>
            )}
            {errorMessage && (
                <div className="error-alert">
                    {errorMessage}
                </div>
            )}
            {results.length > 0 && (
                <ResultsPreview results={results} searchParams={searchParams} />
            )}
            <DownloadModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onDownload={handleDownload} />
        </div>
    );
};

function LoadingMessages() {
    const [step, setStep] = React.useState(0);
    React.useEffect(() => {
        const steps = [
            'Buscando contactos...',
            'Puede tardar unos segundos...',
            'Recopilando información en línea...'
        ];
        const interval = setInterval(() => {
            setStep((prev) => (prev + 1) % steps.length);
        }, 1800);
        return () => clearInterval(interval);
    }, []);
    const steps = [
        'Buscando contactos...',
        'Puede tardar unos segundos...',
        'Recopilando información en línea...'
    ];
    return <p>{steps[step]}</p>;
}

export default Home;
